/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
'use client'

import { useState } from 'react'
import {
    dbExpensesToSteps,
    formatMoney,
    getLastEntry,
    getMonthlyPassiveIncomeFromNavUpdate
} from "../utils/utils";
import Expenses from "../components/ExpensesNew";
import ExpenseDeleteConfirmation from "../components/ExpenseDeleteConfirmation";
import {ExepenseUpdateSidebar} from "../components/ExepenseUpdateSidebar";
import {NewExpenseSidebar} from "../components/NewExpenseSidebar";
import {UpdateInvestmentAmountSidebar} from "../components/UpdateInvestmentAmountSidebar";
import {InvestmentPlanSidebar} from "../components/InvestementPlanSidebar";
import {InvestmentPlanAmountSidebar} from "../components/Sidebars/InvestmentPlanAmountSidebar";
import GuidePopup from "../components/GuidePopup";
import {
    BanknotesIcon,
    BuildingLibraryIcon,
    CreditCardIcon
} from "@heroicons/react/16/solid";
import {PassiveIncomeSidebar} from "../components/PassiveIncomeSidebar";
import {useDemoPassiveIncomeEntries} from "../hooks/useDemoPassiveIncomeEntries";
import useDemoUserDataStore from "../hooks/useDemoUserDataStore";
import {useDemoPortfolioNavUpdates} from "../hooks/useDemoPortfolioNavUpdates";
import useDemoExpenses from "../hooks/useDemoExpenses";
import React from 'react';
import {Layout} from "../components/Layout";
import {ExpenseShowSidebar} from "../components/ExpenseShowSidebar";
import KaizenInfoModal from "../components/KaizenInfoModal";

const DEFAULT_YIELD = 4

function DemoChecklist({ userData, expenses, portfolioNavStore, setIsNewExpenseSidebarOpen, monthlyPassiveIncome, isKaizenInfoModalOpen, setIsKaizenInfoModalOpen }) {
    const [showCompletedSteps, setShowCompletedSteps] = React.useState(false);
    
    const hasPortfolioValue = portfolioNavStore?.navUpdates?.length > 0;
    const hasSmallExpense = expenses?.some(expense => expense.amountInCents < 5000); // < $50
    const hasUtilityExpense = expenses?.some(expense => expense.amountInCents >= 5000 && expense.amountInCents < 50000); // $50-$500
    const hasRentExpense = expenses?.some(expense => expense.amountInCents >= 50000); // >= $500
    const hasMonthlyPlan = userData?.investmentPlanAmount > 0;
    
    // Check if 3 or more expenses are added (regardless of type)
    const hasThreeExpenses = expenses?.length >= 3;

    const checklistItems = [
        {
            id: 'learn-kaizen',
            title: 'Learn more about what Kaizen FI is about',
            description: 'Discover the philosophy behind step-by-step financial independence',
            completed: localStorage.getItem('kaizen-info-viewed') === 'true',
            action: () => {
                setIsKaizenInfoModalOpen(true);
                localStorage.setItem('kaizen-info-viewed', 'true');
            }
        },
        {
            id: 'portfolio',
            title: 'Add your investment portfolio value',
            description: 'Total amount of money you have currently invested',
            completed: hasPortfolioValue,
            action: () => {
                const portfolioElement = document.getElementById('portfolioValue');
                if (portfolioElement) portfolioElement.click();
            }
        },
        {
            id: 'monthly-withdrawal',
            title: 'Check your monthly withdrawal amount',
            description: 'Click to check what monthly withdrawal amount your portfolio now generates',
            completed: localStorage.getItem('demo-passive-income-opened') === 'true',
            action: () => {
                const withdrawalElement = document.getElementById('monthlyWithdrawalNumber');
                if (withdrawalElement) withdrawalElement.click();
            }
        },
        {
            id: 'monthly-plan',
            title: 'Set up monthly investment plan',
            description: 'Build the habit of investing regularly to stay on track toward financial independence',
            completed: hasMonthlyPlan,
            action: () => {
                const planElement = document.getElementById('monthlyInvestmentPlan');
                if (planElement) planElement.click();
            }
        },
        {
            id: 'add-expenses',
            title: 'Add 3 expenses',
            description: 'Add different types of monthly expenses to see how your investment income can cover them',
            completed: hasThreeExpenses,
            action: () => {
                setIsNewExpenseSidebarOpen(true);
            },
            subItems: [
                {
                    title: 'Monthly subscriptions',
                    description: 'Netflix, Spotify, gym membership, etc.',
                    completed: hasSmallExpense
                },
                {
                    title: 'Utility bill',
                    description: 'Electricity, internet, or phone bill',
                    completed: hasUtilityExpense
                },
                {
                    title: 'Rent/mortgage',
                    description: 'Your largest monthly housing expense',
                    completed: hasRentExpense
                },
                {
                    title: 'Groceries',
                    description: 'Weekly or monthly food expenses',
                    completed: hasSmallExpense
                },
                {
                    title: 'Hobby / guilt-free spending budget',
                    description: 'Something you enjoy spending money on',
                    completed: hasSmallExpense
                }
            ]
        },
        {
            id: 'sign-up',
            title: 'Sign up for a free account',
            description: 'Create an account to save your data and unlock all features',
            completed: false, // This will always be false in demo mode
            isSignUp: true, // Special flag for sign-up styling
            action: () => {
                window.location.href = '/signup';
            }
        }
    ];

    const completedCount = checklistItems.filter(item => item.completed).length;
    const totalCount = checklistItems.length;
    
    // Find the next incomplete step (excluding signup)
    const nextStep = checklistItems.find(item => !item.completed && item.id !== 'sign-up');
    const signupStep = checklistItems.find(item => item.id === 'sign-up');
    const completedSteps = checklistItems.filter(item => item.completed);
    
    // Only show signup step if all other steps are completed
    const allOtherStepsCompleted = checklistItems.filter(item => item.id !== 'sign-up').every(item => item.completed);
    const shouldShowSignupStep = signupStep && allOtherStepsCompleted;

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
            {/* Header Section */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-gray-900">📋 Onboarding Checklist</h3>
                    <span className="text-sm font-semibold text-gray-700 bg-gray-100 px-3 py-1 rounded-full">
                        {completedCount}/{totalCount - 1}
                    </span>
                </div>
                
                <p className="text-sm text-gray-600">
                    Take these small steps to build your path to financial independence, the Kaizen way.
                </p>
            </div>

            {/* Next Step - Always Visible */}
            {nextStep && (
                <div className="mb-4">
                    <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">
                        Next Step
                    </div>
                    <div
                        className="group relative bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl shadow-sm transition-all duration-300 overflow-hidden border border-indigo-200 hover:border-indigo-300 hover:shadow-lg"
                    >
                        <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-xl opacity-30 -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform duration-500 bg-gradient-to-br from-indigo-100 via-purple-100 to-transparent" />
                        
                        <div className="relative p-5">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-4">
                                    <div className="w-8 h-8 border-2 border-indigo-300 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 shadow-sm"></div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-lg font-semibold text-indigo-900 mb-1">
                                        {nextStep.title}
                                    </h4>
                                    <p className="text-sm text-indigo-600 mb-3">
                                        {nextStep.description}
                                    </p>
                                    <button
                                        onClick={nextStep.action}
                                        className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                                    >
                                        Get Started
                                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Signup Card - Only visible when all other steps are completed */}
            {shouldShowSignupStep && (
                <div className="mb-4">
                    <div className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">
                        Ready to Launch Your Journey?
                    </div>
                    <div
                        className="group relative bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl shadow-sm transition-all duration-300 overflow-hidden border border-purple-200 hover:border-purple-300 hover:shadow-lg"
                    >
                        <div className="absolute top-0 right-0 w-24 h-24 rounded-full blur-xl opacity-30 -translate-y-6 translate-x-6 group-hover:scale-110 transition-transform duration-500 bg-gradient-to-br from-purple-100 via-pink-100 to-transparent" />
                        
                        <div className="relative p-5">
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-4">
                                    <div className="w-8 h-8 border-2 border-purple-300 rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 shadow-sm"></div>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-lg font-semibold text-purple-900 mb-1">
                                        {signupStep.title}
                                    </h4>
                                    <p className="text-sm text-purple-600 mb-3">
                                        {signupStep.description}
                                    </p>
                                    <button
                                        onClick={signupStep.action}
                                        className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
                                    >
                                        Create Free Account
                                        <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Completed Steps - Collapsible */}
            {completedSteps.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                    {/* Full Width Progress Bar */}
                    <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner mb-4">
                        <div 
                            className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 transition-all duration-700 ease-out shadow-sm"
                            style={{ width: `${(completedCount / (totalCount - 1)) * 100}%` }}
                        ></div>
                    </div>
                    
                    <div className="flex items-center justify-between mb-2">
                        <button
                            onClick={() => setShowCompletedSteps(!showCompletedSteps)}
                            className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                        >
                            <svg 
                                className={`w-4 h-4 transition-transform ${showCompletedSteps ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            {showCompletedSteps ? 'Hide' : 'Show'} completed steps ({completedSteps.length})
                        </button>
                        
                    </div>
                    
                    {showCompletedSteps && (
                        <div className="mt-3 space-y-3">
                            {completedSteps.map((item) => (
                                <div
                                    key={item.id}
                                    className="group relative bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg shadow-sm transition-all duration-300 overflow-hidden border border-emerald-200"
                                >
                                    <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-xl opacity-20 -translate-y-4 translate-x-4 bg-gradient-to-br from-emerald-100 via-teal-100 to-transparent" />
                                    
                                    <div className="relative p-4">
                                        <div className="flex items-start">
                                            <div className="flex-shrink-0 mr-3">
                                                <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                                                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                </div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-semibold text-emerald-900">
                                                    {item.title}
                                                </h4>
                                                <p className="text-xs text-emerald-600">
                                                    {item.description}
                                                </p>
                                            </div>
                                        </div>
                                        
                                        {/* Sub-items for completed steps */}
                                        {item.subItems && (
                                            <div className="mt-3 ml-9 space-y-1">
                                                {item.subItems.map((subItem, index) => (
                                                    <div key={index} className="flex items-center gap-2">
                                                        <div className="flex-shrink-0">
                                                            {subItem.completed ? (
                                                                <div className="w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                                                                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                </div>
                                                            ) : (
                                                                <div className="w-4 h-4 border border-emerald-300 rounded-full bg-white"></div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className={`text-xs font-medium ${
                                                                subItem.completed ? 'text-emerald-800' : 'text-emerald-600'
                                                            }`}>
                                                                {subItem.title}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stats(props) {
    return <div className="space-y-4">
        <div
            id="portfolioValue"
            className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 hover:border-indigo-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer"
            onClick={props.onClick}
        >
            {/* Decorative gradient background */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-30 -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform duration-500 bg-gradient-to-br from-indigo-100 via-purple-100 to-transparent" />
            
            <div className="relative p-6">
                <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <BuildingLibraryIcon className="h-5 w-5 text-white"/>
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                                Portfolio Value
                            </h4>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                            {formatMoney(props.amount)}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div
            id="monthlyWithdrawalNumber"
            className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 hover:border-indigo-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer"
            onClick={() => props.setIsPassiveIncomeSidebarOpen(true)}
        >
            {/* Decorative gradient background */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-30 -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform duration-500 bg-gradient-to-br from-indigo-100 via-purple-100 to-transparent" />
            
            <div className="relative p-6">
                <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <BanknotesIcon className="h-5 w-5 text-white"/>
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                                Monthly Withdrawal
                            </h4>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                            {formatMoney(props.amount1)}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div
            id="monthlyInvestmentPlan"
            className="group relative bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden border border-gray-200 hover:border-indigo-200 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer"
            onClick={props.onClick1}
        >
            {/* Decorative gradient background */}
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl opacity-30 -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform duration-500 bg-gradient-to-br from-indigo-100 via-purple-100 to-transparent" />
            
            <div className="relative p-6">
                <div className="flex items-start">
                    <div className="flex-shrink-0 mr-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                            <CreditCardIcon className="h-5 w-5 text-white"/>
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="mb-2">
                            <h4 className="text-lg font-semibold text-gray-900">
                                Monthly Investment Plan
                            </h4>
                        </div>
                        <p className="text-2xl font-bold text-gray-900">
                            {props.userData.investmentPlanAmount ? formatMoney(props.userData.investmentPlanAmount) : formatMoney(0)}
                        </p>
                    </div>
                </div>
            </div>
        </div>

        {/* <div className="mt-6 p-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
            <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20">
                        <span className="text-2xl">🪴</span>
                    </div>
                </div>
                <div className="ml-4">
                    <h4 className="text-lg font-semibold text-indigo-900">
                        This is demo mode
                    </h4>
                    <p className="text-sm text-indigo-700 mt-1">
                        Your data is temporarily stored in your browser, and some features are limited.
                    </p>
                </div>
            </div>
            <p className="text-sm text-indigo-700 mb-4">
                To store your data reliably and unlock all features, including <b>goal tracking</b>, settings and be able to use the app across devices, <b>create a free account</b>.
                <br/><br/>
                <span className="font-semibold">✨ Your demo data will be automatically migrated to your new account!</span>
            </p>
            <button
                onClick={() => window.location.href = "/signup"}
                type="button"
                className="w-full rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
                Sign up for free
            </button>
        </div> */}
    </div>;
}

export function Demo() {

    const {
        userData,
        updateInvestmentPlanAmount,
        addInvestmentPlanEntry,
        removeInvestmentPlanEntry,
        syncUserData
    } = useDemoUserDataStore()
    const portfolioNavStore = useDemoPortfolioNavUpdates()

    const [isPassiveIncomeSidebarOpen, setIsPassiveIncomeSidebarOpen] = useState(false)

    const [isNewExpenseSidebarOpen, setIsNewExpenseSidebarOpen] = useState(false)
    const [isUpdateExpenseSidebarOpen, setIsUpdateExpenseSidebarOpen] = useState(false)
    const [isDeleteExpenseSidebarOpen, setIsDeleteExpenseSidebarOpen] = useState(false)
    const [isUpdateInvestementAmountSidebarOpen, setIsInvestementAmountSidebarOpen] = useState(false)
    const [isInvestmentPlanSidebarOpen, setIsInvestmentPlanSidebarOpen] = useState(false)
    const [isInvestmentPlanAmountSidebarOpen, setIsInvestmentPlanAmountSidebarOpen] = useState(false)
    const [isGuidePopupOpen, setIsGuidePopupOpen] = useState(false)
    const [isInsightModalOpen, setIsInsightModalOpen] = useState(false)
    const [isExpenseShowSidebarOpen, setIsExpenseShowSidebarOpen] = useState(false)
    const [isKaizenInfoModalOpen, setIsKaizenInfoModalOpen] = useState(false)
    const [selectedExpense, setSelectedExpense] = useState(null)
    const [newlyCreatedExpense, setNewlyCreatedExpense] = useState(null)
    // const updateExpenseSidebarData = useUpdateExpenseSidebar()
    const expenseStore = useDemoExpenses()
    const passiveIncomeStore = useDemoPassiveIncomeEntries()
    const [cumulative, setCumulative] = useState(false)

    const {expenses, currentExpense, controls} = expenseStore


    const liquidPassiveIncome = portfolioNavStore && userData ? getMonthlyPassiveIncomeFromNavUpdate(portfolioNavStore.navUpdates, userData.yield || DEFAULT_YIELD) : 0
    const monthlyPassiveIncome = liquidPassiveIncome + (passiveIncomeStore.passiveIncomeEntriesTotal / 100)

    const lastEntry = getLastEntry(portfolioNavStore.navUpdates)
    const lastEntryAmount = (lastEntry?.amountInCents || 0) / 100

    const updateExpense = (id) => {
        const expense = expenses.find((e) => e.id === id)
        console.log('App:updateExpense:', expense)
        controls.selectExpense(expense)
        setIsUpdateExpenseSidebarOpen(true)
    }

    const openInsightModal = (step) => {
        // Handle total expenses case
        if (step.expenseType === 'total') {
            setSelectedExpense(step)
            setIsInsightModalOpen(true)
            return
        }
        
        // Find the original expense object from the expenseId
        const originalExpense = expenses.find(exp => exp.id === step.expenseId)
        
        if (originalExpense) {
            setSelectedExpense(originalExpense)
            setIsInsightModalOpen(true)
        } else {
            console.error('Could not find original expense for step:', step)
        }
    }


    let restOfIncome = monthlyPassiveIncome
    const expensesAsSteps = expenses.length ? dbExpensesToSteps(expenses, restOfIncome, cumulative) : []
    // Total monthly expenses
    // const total = expensesTotal(data)

    return (
        <Layout pageSlug="dashboard" mode={"demo"}
                aside={<Stats onClick={() => setIsInvestementAmountSidebarOpen(true)} amount={lastEntryAmount}
                              amount1={monthlyPassiveIncome} onClick1={() => setIsInvestmentPlanAmountSidebarOpen(true)}
                              setIsPassiveIncomeSidebarOpen={setIsPassiveIncomeSidebarOpen}
                              userData={userData}/>}>
            <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-6">
                <DemoChecklist 
                    userData={userData}
                    expenses={expenses}
                    portfolioNavStore={portfolioNavStore}
                    setIsNewExpenseSidebarOpen={setIsNewExpenseSidebarOpen}
                    monthlyPassiveIncome={monthlyPassiveIncome}
                    isKaizenInfoModalOpen={isKaizenInfoModalOpen}
                    setIsKaizenInfoModalOpen={setIsKaizenInfoModalOpen}
                />
                <div className="lg:hidden mb-6">
                    <Stats onClick={() => setIsInvestementAmountSidebarOpen(true)} amount={lastEntryAmount}
                           amount1={monthlyPassiveIncome} onClick1={() => setIsInvestmentPlanAmountSidebarOpen(true)}
                           setIsPassiveIncomeSidebarOpen={setIsPassiveIncomeSidebarOpen}
                           userData={userData}/>
                </div>
                <Expenses mode={"demo"}
                    monthlyPassiveIncome={monthlyPassiveIncome} expenseStore={expenseStore}
                          openExpenseForm={setIsNewExpenseSidebarOpen}
                          setIsGuidePopupOpen={setIsGuidePopupOpen}
                          openInsightModal={openInsightModal}
                          expensesAsSteps={expensesAsSteps}
                          cumulative={cumulative}
                          setCumulative={setCumulative}
                          openDeleteExpenseDialog={(expense) => {
                              controls.selectExpense(expense)
                              setIsDeleteExpenseSidebarOpen(true)
                          }} expenses={expenses} topExpense={() => {
                }}
                          updateExpense={updateExpense}
                          setIsInvestmentPlanSidebarOpen={setIsInvestmentPlanSidebarOpen}
                />
            </div>

            <ExpenseDeleteConfirmation expense={currentExpense} controls={controls}
                                       isOpen={isDeleteExpenseSidebarOpen}
                                       setIsOpen={setIsDeleteExpenseSidebarOpen}/>
            {currentExpense && <ExepenseUpdateSidebar expense={currentExpense} controls={controls}
                                                      isOpen={isUpdateExpenseSidebarOpen}
                                                      setIsOpen={setIsUpdateExpenseSidebarOpen}/>}
            <NewExpenseSidebar controls={controls} isOpen={isNewExpenseSidebarOpen}
                               setIsOpen={setIsNewExpenseSidebarOpen}
                               onExpenseCreated={(expense) => {
                                   setNewlyCreatedExpense(expense)
                                   setIsExpenseShowSidebarOpen(true)
                               }}/>
            <UpdateInvestmentAmountSidebar
                {...{
                    portfolioNavStore,
                    isOpen: isUpdateInvestementAmountSidebarOpen,
                    setIsOpen: setIsInvestementAmountSidebarOpen,
                }}
            />
            {userData && <PassiveIncomeSidebar
                {...{
                    userData,
                    isOpen: isPassiveIncomeSidebarOpen,
                    setIsOpen: setIsPassiveIncomeSidebarOpen,
                    liquidPassiveIncome,
                    monthlyPassiveIncome,
                    portfolioNav: lastEntryAmount,
                    passiveIncomeEntries: passiveIncomeStore.passiveIncomeEntries,
                    addPassiveIncomeEntry: passiveIncomeStore.addPassiveIncomeEntry,
                    removePassiveIncomeEntry: passiveIncomeStore.removePassiveIncomeEntry,
                }}
            />}
            {userData && <InvestmentPlanSidebar
                {...{
                    userData,
                    syncUserData,
                    addInvestmentPlanEntry,
                    removeInvestmentPlanEntry,
                    isOpen: isInvestmentPlanSidebarOpen,
                    setIsOpen: setIsInvestmentPlanSidebarOpen,
                    portfolioNavStore
                }} />}
            {userData && <InvestmentPlanAmountSidebar
                {...{
                    amount: userData.investmentPlanAmount,
                    userData,
                    updateInvestmentPlanAmount: updateInvestmentPlanAmount,
                    isOpen: isInvestmentPlanAmountSidebarOpen,
                    setIsOpen: setIsInvestmentPlanAmountSidebarOpen,
                }} />}
            {/* {userData && userData.investmentPlanAmount && <GuidePopup
                cumulative={cumulative}
                expensesAsSteps={expensesAsSteps}
                expenseStore={expenseStore}
                portfolioNavStore={portfolioNavStore}
                monthlyPassiveIncome={monthlyPassiveIncome}
                userData={userData}
                isOpen={isGuidePopupOpen} setIsOpen={setIsGuidePopupOpen}/>} */}
            {selectedExpense && <ExpenseShowSidebar
                expense={selectedExpense}
                isOpen={isInsightModalOpen}
                setIsOpen={setIsInsightModalOpen}
                monthlyIncome={monthlyPassiveIncome}
                monthlyInvestmentPlan={userData?.investmentPlanAmount || 0}
                withdrawalRate={userData?.yield || DEFAULT_YIELD}
                onEditExpense={(expense) => updateExpense(expense.id)}
            />}
            {newlyCreatedExpense && <ExpenseShowSidebar
                expense={newlyCreatedExpense}
                isOpen={isExpenseShowSidebarOpen}
                setIsOpen={setIsExpenseShowSidebarOpen}
                monthlyIncome={monthlyPassiveIncome}
                monthlyInvestmentPlan={userData?.investmentPlanAmount || 0}
                withdrawalRate={userData?.yield || DEFAULT_YIELD}
                onEditExpense={(expense) => updateExpense(expense.id)}
            />}
            {/*<IntroPopup />*/}
            <KaizenInfoModal 
                isOpen={isKaizenInfoModalOpen} 
                setIsOpen={setIsKaizenInfoModalOpen} 
            />
            
            {/* Fixed Footer Sign Up Button */}
            <div className="fixed bottom-0 left-0 right-0 z-30 bg-gradient-to-r from-purple-50 to-pink-50 border-t border-purple-200 shadow-lg">
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-purple-900">
                                Sign up for a free account
                            </h3>
                            <p className="text-sm text-purple-600">
                                Create an account to save your data and unlock all features
                            </p>
                        </div>
                        <div className="ml-6">
                            <button
                                onClick={() => window.location.href = '/signup'}
                                className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md whitespace-nowrap"
                            >
                                Create Free Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    )
}
